import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import "bootstrap-icons/font/bootstrap-icons.css";

import { APP_ROUTES } from "../constants/appRoutes";
import { GUEST_AUTH_MODAL, useAuthModal } from "../context/AuthModalContext";
import { trackCtaClicked } from "../analytics/analytics";
import { ANALYTICS_CTAS } from "../analytics/analytics.constants";
import {
    formatUsage,
    getDisplayPlanName,
    isCampaignQuotaExhausted,
} from "../utils/normalizePlan";

function PlanUsageRows({ planData }) {
    if (!planData?.hasPlans) return null;

    return (
        <div className="space-y-2 text-sm text-slate-600">
            <p className="mb-0 flex items-center justify-between gap-3">
                <span className="inline-flex min-w-0 items-center gap-2">
                    <i className="bi bi-megaphone text-slate-400" aria-hidden />
                    Campaigns
                </span>
                <span className="shrink-0 font-semibold tabular-nums text-slate-800">
                    {formatUsage(planData.campaign)}
                </span>
            </p>
            <p className="mb-0 flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-2">
                    <i className="bi bi-file-earmark-text text-slate-400" aria-hidden />
                    Posts
                </span>
                <span className="font-semibold tabular-nums text-slate-800">
                    {formatUsage(planData.post)}
                </span>
            </p>
        </div>
    );
}

function PlanSummaryDetails({ planData, planLoading, planLoaded }) {
    const user = useSelector((state) => state.auth.user);
    const isGuest = !user;
    const planLabel = getDisplayPlanName(planData, { loading: planLoading, loaded: planLoaded });
    const noActivePlan = planLoaded && !planLoading && (!planData || !planData.hasPlans);
    const campaignExhausted = isCampaignQuotaExhausted(planData);
    const showPlanAlert = noActivePlan || campaignExhausted;

    if (isGuest) {
        return (
            <p className="mb-0 text-sm text-slate-600">
                Sign in to start your trial and create campaigns and posts.
            </p>
        );
    }

    if (planLoading) {
        return <p className="mb-0 text-sm text-slate-500">Loading plan details…</p>;
    }

    if (showPlanAlert) {
        return (
            <div className="space-y-3">
                <div className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-3">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-red-700">
                        <i className="bi bi-exclamation-triangle" aria-hidden />
                    </span>
                    <div className="min-w-0">
                        <p className="mb-0 text-xs font-semibold uppercase tracking-wide text-red-800">
                            {noActivePlan ? "No plan" : "Quota exhausted"}
                        </p>
                        <p className="mb-0 mt-0.5 text-sm text-red-700/90">
                            {noActivePlan
                                ? "Subscribe to a plan to create campaigns and posts."
                                : "Your campaign quota is used up for this period."}
                        </p>
                    </div>
                </div>
                <PlanUsageRows planData={planData} />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                    <i className="bi bi-gem" aria-hidden />
                </span>
                <div className="min-w-0">
                    <p className="mb-0 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Current plan
                    </p>
                    <p className="mb-0 truncate text-base font-semibold text-slate-900">{planLabel}</p>
                </div>
            </div>
            <PlanUsageRows planData={planData} />
        </div>
    );
}

function MobilePlanModal({ open, onClose, planData, planLoading, planLoaded }) {
    const user = useSelector((state) => state.auth.user);
    const isGuest = !user;
    const { openAuthModal } = useAuthModal();

    useEffect(() => {
        if (!open) return;

        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };

        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, onClose]);

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-900/50 p-0 sm:items-center sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-plan-modal-title"
            onClick={onClose}
        >
            <div
                className="w-full max-w-sm rounded-t-2xl bg-white shadow-xl sm:rounded-2xl"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                    <h2 id="mobile-plan-modal-title" className="mb-0 text-base font-semibold text-slate-900">
                        Plan details
                    </h2>
                    <button
                        type="button"
                        className="rounded-md p-1.5 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800"
                        aria-label="Close plan details"
                        onClick={onClose}
                    >
                        <i className="bi bi-x-lg text-sm" aria-hidden />
                    </button>
                </div>

                <div className="px-4 py-4">
                    <PlanSummaryDetails
                        planData={planData}
                        planLoading={planLoading}
                        planLoaded={planLoaded}
                    />
                </div>

                <div className="border-t border-slate-200 px-4 py-3">
                    {isGuest ? (
                        <button
                            type="button"
                            onClick={() => {
                                trackCtaClicked(ANALYTICS_CTAS.GET_STARTED);
                                onClose();
                                openAuthModal(GUEST_AUTH_MODAL);
                            }}
                            className="btn btn-dark-app flex w-full items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold"
                        >
                            <i className="bi bi-box-arrow-in-right" aria-hidden />
                            Get started
                        </button>
                    ) : (
                        <Link
                            to={APP_ROUTES.PLANS}
                            onClick={() => {
                                trackCtaClicked(ANALYTICS_CTAS.NAV_UPGRADE);
                                onClose();
                            }}
                            className="btn btn-dark-app flex w-full items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold"
                        >
                            <i className="bi bi-arrow-up-right" aria-hidden />
                            Upgrade
                        </Link>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}

export function MobilePlanPill({ planData, planLoading, planLoaded, className = "" }) {
    const [open, setOpen] = useState(false);
    const user = useSelector((state) => state.auth.user);
    const isGuest = !user;
    const { openAuthModal } = useAuthModal();

    if (isGuest) {
        return (
            <button
                type="button"
                onClick={() => {
                    trackCtaClicked(ANALYTICS_CTAS.GET_STARTED);
                    openAuthModal(GUEST_AUTH_MODAL);
                }}
                className={`inline-flex max-w-[42vw] min-w-0 items-center justify-center gap-1.5 rounded-full border border-black bg-white px-2.5 py-1 text-[10px] font-semibold text-black shadow-none transition hover:bg-black hover:text-white active:scale-95 sm:max-w-none sm:px-3 sm:py-1.5 sm:text-xs ${className}`.trim()}
            >
                <i className="bi bi-box-arrow-in-right shrink-0 text-[10px] sm:text-xs" aria-hidden />
                <span className="truncate">Get started</span>
            </button>
        );
    }

    const planLabel = getDisplayPlanName(planData, { loading: planLoading, loaded: planLoaded });
    const noActivePlan = planLoaded && !planLoading && (!planData || !planData.hasPlans);
    const campaignExhausted = isCampaignQuotaExhausted(planData);
    const isAlert = noActivePlan || campaignExhausted;

    const pillText = planLoading
        ? "Loading…"
        : noActivePlan
          ? "No plan"
          : campaignExhausted
            ? "Exhausted"
            : planLabel;

    return (
        <>
            <button
                type="button"
                onClick={() => {
                    trackCtaClicked(ANALYTICS_CTAS.NAV_PLAN_PILL);
                    setOpen(true);
                }}
                className={`inline-flex max-w-[42vw] min-w-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide transition active:scale-95 sm:max-w-[160px] ${
                    isAlert
                        ? "border-red-200 bg-red-50 text-red-800 hover:bg-red-100/80"
                        : "border-emerald-200/80 bg-emerald-50 text-slate-700 hover:bg-emerald-100/80"
                } ${className}`.trim()}
                aria-label={`View plan details: ${pillText}`}
                aria-haspopup="dialog"
            >
                <span className="truncate">{pillText}</span>
                <i className="bi bi-box-arrow-up-right shrink-0 text-[10px] opacity-80" aria-hidden />
            </button>

            <MobilePlanModal
                open={open}
                onClose={() => setOpen(false)}
                planData={planData}
                planLoading={planLoading}
                planLoaded={planLoaded}
            />
        </>
    );
}

export default function SidebarPlanSummary({
    planData,
    planLoading,
    planLoaded,
    onNavigate,
    className = "",
}) {
    const user = useSelector((state) => state.auth.user);
    const isGuest = !user;
    const { openAuthModal } = useAuthModal();
    const planLabel = getDisplayPlanName(planData, { loading: planLoading, loaded: planLoaded });
    const noActivePlan = planLoaded && !planLoading && (!planData || !planData.hasPlans);
    const campaignExhausted = isCampaignQuotaExhausted(planData);
    const showPlanAlert = noActivePlan || campaignExhausted;

    if (isGuest) {
        return (
            <button
                type="button"
                onClick={() => {
                    trackCtaClicked(ANALYTICS_CTAS.GET_STARTED);
                    onNavigate?.();
                    openAuthModal(GUEST_AUTH_MODAL);
                }}
                className={`btn btn-dark-app flex w-full items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold ${className}`}
            >
                <i className="bi bi-box-arrow-in-right" aria-hidden />
                Get started
            </button>
        );
    }

    if (showPlanAlert) {
        return (
            <div className={`rounded-xl border border-red-300/60 bg-red-200 p-3 ${className}`}>
                <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white/90 text-red-700">
                            <i className="bi bi-exclamation-triangle" aria-hidden />
                        </div>
                        <p className="mb-0 text-[11px] font-semibold uppercase tracking-wide text-red-900">
                            {noActivePlan ? "No plan" : "Exhausted"}
                        </p>
                    </div>
                    <Link
                        to={APP_ROUTES.PLANS}
                        onClick={() => {
                            trackCtaClicked(ANALYTICS_CTAS.NAV_UPGRADE);
                            onNavigate?.();
                        }}
                        className="inline-flex items-center rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-slate-900 shadow-sm transition hover:bg-red-50"
                    >
                        <i className="bi bi-arrow-up-right me-1" aria-hidden />
                        Upgrade
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`rounded-xl border border-slate-200/55 bg-green-100/35 p-3 backdrop-blur-md ${className}`}
        >
            <div className="mb-2 flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-white/65 text-slate-600 backdrop-blur-sm">
                    <i className="bi bi-gem" aria-hidden />
                </div>
                <p className="mb-0 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {planLabel}
                </p>
            </div>
            {planData?.hasPlans ? (
                <div className="mb-3 space-y-1 text-[10px] text-slate-600">
                    <p className="mb-0 flex items-center justify-between gap-2">
                        <span className="inline-flex min-w-0 items-center gap-1">
                            <i className="bi bi-megaphone text-slate-400" aria-hidden />
                            Campaigns
                        </span>
                        <span className="shrink-0 font-semibold tabular-nums text-slate-800">
                            {formatUsage(planData.campaign)}
                        </span>
                    </p>
                    <p className="mb-0 flex items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-1">
                            <i className="bi bi-file-earmark-text text-slate-400" aria-hidden />
                            Posts
                        </span>
                        <span className="font-semibold tabular-nums text-slate-800">
                            {formatUsage(planData.post)}
                        </span>
                    </p>
                </div>
            ) : null}
            <div className="flex justify-end">
                <Link
                    to={APP_ROUTES.PLANS}
                    onClick={() => {
                        trackCtaClicked(ANALYTICS_CTAS.NAV_UPGRADE);
                        onNavigate?.();
                    }}
                    className="inline-flex items-center rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-900 shadow-sm transition hover:bg-slate-50"
                >
                    <i className="bi bi-arrow-up-right me-1" aria-hidden />
                    Upgrade
                </Link>
            </div>
        </div>
    );
}
